import React from 'react';
import { 
  Document, 
  Page, 
  Text, 
  View, 
  StyleSheet, 
  PDFDownloadLink,
  Link,
  Font
} from '@react-pdf/renderer';
import { Bid, ServiceRequest, formatDate } from 'shared-lib';
import { Button } from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

// Register fonts
Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf', fontWeight: 300 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf', fontWeight: 400 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf', fontWeight: 500 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 700 }
  ]
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    padding: 30,
    fontFamily: 'Roboto'
  },
  header: {
    marginBottom: 20,
    paddingBottom: 10,
    borderBottom: '1px solid #e0e0e0'
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 10,
    color: '#333'
  },
  subtitle: {
    fontSize: 16,
    fontWeight: 500,
    color: '#555',
    marginBottom: 5
  },
  section: {
    marginBottom: 20
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 5,
    color: '#00AB9B'
  },
  text: {
    fontSize: 12,
    color: '#333',
    lineHeight: 1.5,
    marginBottom: 3
  },
  boldText: {
    fontWeight: 700
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5
  },
  label: {
    width: 120,
    fontSize: 12,
    color: '#555'
  },
  value: {
    flex: 1,
    fontSize: 12,
    color: '#333'
  },
  notes: {
    backgroundColor: '#f5f5f5',
    padding: 10,
    borderRadius: 5,
    marginBottom: 10
  },
  notesText: {
    fontSize: 12,
    color: '#333',
    lineHeight: 1.5
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 10,
    color: '#999',
    textAlign: 'center',
    borderTop: '1px solid #e0e0e0',
    paddingTop: 10
  },
  attachmentSection: {
    marginTop: 10
  },
  attachmentRow: {
    flexDirection: 'row',
    marginBottom: 5,
    alignItems: 'center'
  },
  attachmentLink: {
    fontSize: 10,
    color: '#0066cc',
    textDecoration: 'underline'
  },
  bidAmount: {
    fontSize: 16,
    fontWeight: 700,
    color: '#00AB9B',
    marginBottom: 5
  },
  status: {
    fontSize: 12,
    color: '#fff',
    padding: '3 8',
    borderRadius: 10,
    alignSelf: 'flex-start',
    textTransform: 'uppercase'
  },
  statusAccepted: {
    backgroundColor: '#00AB9B'
  },
  statusPending: {
    backgroundColor: '#F57C00'
  },
  bidCard: {
    marginBottom: 20,
    padding: 15,
    borderRadius: 5,
    border: '1px solid #e0e0e0'
  },
  acceptedBidCard: {
    borderColor: '#00AB9B',
    backgroundColor: 'rgba(0, 171, 155, 0.05)'
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 30,
    right: 30,
    color: '#999'
  },
  pageBreak: {
    height: 1,
    marginTop: 30,
    marginBottom: 30,
    borderBottom: '1px dashed #e0e0e0'
  },
  summary: {
    backgroundColor: '#f9f9f9',
    padding: 10,
    marginBottom: 20,
    borderRadius: 5,
    border: '1px solid #e0e0e0'
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 10,
    color: '#333'
  }
});

// Bid Card Component for PDF
const BidCard = ({ bid, index }: { bid: Bid; index: number }) => {
  return (
    <View style={[
      styles.bidCard, 
      bid.status === 'accepted' ? styles.acceptedBidCard : {}
    ]}>
      <View style={styles.row}>
        <Text style={styles.sectionTitle}>BID #{index + 1}</Text>
        <Text style={[
          styles.status, 
          bid.status === 'accepted' ? styles.statusAccepted : styles.statusPending
        ]}>
          {bid.status === 'accepted' ? 'ACCEPTED' : 'PENDING'}
        </Text>
      </View>
      
      <View style={styles.row}>
        <Text style={styles.label}>Bid Amount:</Text>
        <Text style={styles.bidAmount}>${bid.amount?.toFixed(2)}</Text>
      </View>
      
      <View style={styles.row}>
        <Text style={styles.label}>Submitted:</Text>
        <Text style={styles.value}>{formatDate(bid.createdAt)}</Text>
      </View>
      
      <View style={styles.row}>
        <Text style={styles.label}>Earliest Start:</Text>
        <Text style={styles.value}>{bid.earliestStartDate ? formatDate(bid.earliestStartDate) : 'Not specified'}</Text>
      </View>
      
      <View style={styles.row}>
        <Text style={styles.label}>Project Duration:</Text>
        <Text style={styles.value}>{bid.projectDuration ? `${bid.projectDuration} days` : 'Not specified'}</Text>
      </View>
      
      {(bid.notes && bid.notes.trim() !== '') && (
        <View style={styles.notes}>
          <Text style={styles.sectionTitle}>Vendor Notes:</Text>
          <Text style={styles.notesText}>{bid.notes}</Text>
        </View>
      )}
      
      {/* Attachments section */}
      {bid.attachments && bid.attachments.length > 0 && (
        <View style={styles.attachmentSection}>
          <Text style={styles.sectionTitle}>ATTACHMENTS</Text>
          
          {bid.attachments.map((attachment, index) => (
            <View key={index} style={styles.attachmentRow}>
              <Text style={styles.text}>
                {attachment.fileName || `Attachment ${index + 1}`} - 
                <Link src={attachment.url} style={styles.attachmentLink}>View</Link>
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

// Summary Component for PDF
const BidsSummary = ({ bids }: { bids: Bid[] }) => {
  const acceptedBid = bids.find(bid => bid.status === 'accepted');
  const pendingBids = bids.filter(bid => bid.status !== 'accepted');
  const lowestBid = [...bids].sort((a, b) => (a.amount || 0) - (b.amount || 0))[0];
  const highestBid = [...bids].sort((a, b) => (b.amount || 0) - (a.amount || 0))[0];

  // Calculate average excluding the highest and lowest if there are more than 3 bids
  let averageBid = 0;
  if (bids.length > 0) {
    if (bids.length > 3) {
      // Exclude highest and lowest
      const sortedBids = [...bids].sort((a, b) => (a.amount || 0) - (b.amount || 0));
      const middleBids = sortedBids.slice(1, sortedBids.length - 1);
      const sum = middleBids.reduce((acc, bid) => acc + (bid.amount || 0), 0);
      averageBid = sum / middleBids.length;
    } else {
      // Use all bids for average
      const sum = bids.reduce((acc, bid) => acc + (bid.amount || 0), 0);
      averageBid = sum / bids.length;
    }
  }
  
  // Calculate average project duration
  let averageDuration = 0;
  if (bids.length > 0) {
    const sum = bids.reduce((acc, bid) => acc + (bid.projectDuration || 0), 0);
    averageDuration = sum / bids.length;
  }

  return (
    <View style={styles.summary}>
      <Text style={styles.summaryTitle}>SUMMARY</Text>
      
      <View style={styles.row}>
        <Text style={styles.label}>Total Bids:</Text>
        <Text style={styles.value}>{bids.length}</Text>
      </View>
      
      {acceptedBid && (
        <View style={styles.row}>
          <Text style={styles.label}>Winning Bid:</Text>
          <Text style={styles.value}>${acceptedBid.amount?.toFixed(2)}</Text>
        </View>
      )}
      
      <View style={styles.row}>
        <Text style={styles.label}>Lowest Bid:</Text>
        <Text style={styles.value}>${lowestBid?.amount?.toFixed(2)}</Text>
      </View>
      
      <View style={styles.row}>
        <Text style={styles.label}>Highest Bid:</Text>
        <Text style={styles.value}>${highestBid?.amount?.toFixed(2)}</Text>
      </View>
      
      <View style={styles.row}>
        <Text style={styles.label}>Average Bid:</Text>
        <Text style={styles.value}>${averageBid.toFixed(2)}</Text>
      </View>
      
      <View style={styles.row}>
        <Text style={styles.label}>Avg. Duration:</Text>
        <Text style={styles.value}>{averageDuration.toFixed(0)} days</Text>
      </View>
      
      <View style={styles.row}>
        <Text style={styles.label}>Pending Bids:</Text>
        <Text style={styles.value}>{pendingBids.length}</Text>
      </View>
    </View>
  );
};

// PDF Document Component
const AllBidsPDF = ({ bids, serviceRequest }: { bids: Bid[], serviceRequest: ServiceRequest }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Service Request Bids</Text>
          <Text style={styles.subtitle}>Service Request #{serviceRequest.id}</Text>
          <Text style={styles.text}>Generated on {new Date().toLocaleDateString()}</Text>
        </View>
        
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>SERVICE REQUEST DETAILS</Text>
          
          <View style={styles.row}>
            <Text style={styles.label}>Service Type:</Text>
            <Text style={styles.value}>{serviceRequest.serviceName || 'Not specified'}</Text>
          </View>
          
          <View style={styles.row}>
            <Text style={styles.label}>Status:</Text>
            <Text style={styles.value}>{serviceRequest.status}</Text>
          </View>
          
          <View style={styles.row}>
            <Text style={styles.label}>Created:</Text>
            <Text style={styles.value}>{formatDate(serviceRequest.createdAt)}</Text>
          </View>
          
          {serviceRequest.preferredDate && (
            <View style={styles.row}>
              <Text style={styles.label}>Preferred Date:</Text>
              <Text style={styles.value}>{formatDate(serviceRequest.preferredDate)}</Text>
            </View>
          )}
          
          <View style={styles.row}>
            <Text style={styles.label}>Location:</Text>
            <Text style={styles.value}>
              {[
                serviceRequest.address, 
                serviceRequest.city, 
                serviceRequest.state, 
                serviceRequest.zipCode
              ].filter(Boolean).join(', ')}
            </Text>
          </View>
        </View>
        
        {bids.length > 0 && (
          <BidsSummary bids={bids} />
        )}
        
        <Text style={styles.sectionTitle}>ALL BIDS ({bids.length})</Text>
        
        {bids.map((bid, index) => (
          <BidCard key={index} bid={bid} index={index} />
        ))}
        
        <View style={styles.footer}>
          <Text>Generated by Rent Ready Bids | {new Date().toLocaleString()}</Text>
        </View>
        
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `${pageNumber} / ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  );
};

interface AllBidsPdfExportProps {
  bids: Bid[];
  serviceRequest: ServiceRequest;
}

// Export Button Component
const AllBidsPdfExport: React.FC<AllBidsPdfExportProps> = ({ bids, serviceRequest }) => {
  const fileName = `all-bids-${serviceRequest.id}-${new Date().getTime()}.pdf`;
  
  return (
    <PDFDownloadLink 
      document={<AllBidsPDF bids={bids} serviceRequest={serviceRequest} />} 
      fileName={fileName}
      style={{ textDecoration: 'none' }}
    >
      {({ blob, url, loading, error }) => (
        <Button
          variant="contained"
          size="small"
          startIcon={<PdfIcon />}
          disabled={loading}
          sx={{
            backgroundColor: '#00AB9B',
            '&:hover': {
              backgroundColor: '#008C80',
            }
          }}
        >
          {loading ? 'Generating PDF...' : 'Export All Bids'}
        </Button>
      )}
    </PDFDownloadLink>
  );
};

export default AllBidsPdfExport; 